import { StanButton } from '@/components';
import { cn } from '@/lib';
import { useCallback, useState } from 'react';
import IcCheck from '@/assets/icons/ic-check.svg';

interface RecoveryTextInputProps {
    error: string[];
    warning?: string;
    onResetErrors: () => void;
    submitButtonText?: string;
    onSubmit: (text: string) => void;
    onShowOTP: () => void;
    loading?: boolean;
}

// Recovery code is 64 hex characters, displayed as 16 groups of 4 characters
const EXPECTED_CHAR_COUNT = 64;
const GROUP_SIZE = 4;

/**
 * Formats a recovery code string into groups of 4 characters separated by spaces
 * Example: "154AD43D557E47E0" -> "154A D43D 557E 47E0"
 */
function formatRecoveryCode(value: string): string {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // Split into groups of 4 and join with spaces
    const groups: string[] = [];
    for (let i = 0; i < cleaned.length; i += GROUP_SIZE) {
        groups.push(cleaned.slice(i, i + GROUP_SIZE));
    }

    return groups.join(' ');
}

/**
 * Gets the raw character count (without spaces)
 */
function getRawCharCount(value: string): number {
    return value.replace(/\s/g, '').length;
}

export function RecoveryTextInput({
    error,
    warning,
    onResetErrors,
    submitButtonText = 'Continue',
    onSubmit,
    onShowOTP,
    loading = false,
}: RecoveryTextInputProps) {
    const [recoveryText, setRecoveryText] = useState('');

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const rawValue = e.target.value;
            const formatted = formatRecoveryCode(rawValue);
            setRecoveryText(formatted);
            onResetErrors();
        },
        [onResetErrors]
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            e.preventDefault();
            const pastedText = e.clipboardData.getData('text');
            const formatted = formatRecoveryCode(pastedText);
            setRecoveryText(formatted);
            onResetErrors();
        },
        [onResetErrors]
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(recoveryText);
    };

    const openHelpPageInNewTab = () => {
        window.open('https://help.stan.store/article/410-2fa-help', '_blank');
    };

    const hasError = error && error.length > 0;
    const charCount = getRawCharCount(recoveryText);
    const isValidLength = charCount === EXPECTED_CHAR_COUNT;
    const isEmpty = charCount === 0;

    return (
        <form onSubmit={handleSubmit} className='w-full'>
            <div className='mt-3 mb-4 w-full'>
                <div className='mt-3 mb-3 flex flex-col'>
                    {/* Recovery Code Textarea */}
                    <div className='relative'>
                        <textarea
                            id='recovery-text-input'
                            name='recovery-text-input'
                            value={recoveryText}
                            onChange={handleChange}
                            onPaste={handlePaste}
                            placeholder='Paste or type your 64-character recovery code'
                            rows={3}
                            spellCheck={false}
                            autoComplete='off'
                            autoCorrect='off'
                            autoCapitalize='characters'
                            className={cn(
                                'w-full rounded-lg border px-3 py-3',
                                'font-mono text-sm leading-relaxed tracking-wider',
                                'resize-none transition-all duration-200',
                                'placeholder:font-sans placeholder:text-sm placeholder:tracking-normal',
                                'focus:ring-2 focus:ring-offset-0 focus:outline-none',
                                hasError
                                    ? 'border-negative bg-negative-subtle focus:border-negative focus:ring-negative/20'
                                    : 'focus:border-brand-main focus:ring-brand-main/20 border-gray-300 bg-white',
                                'text-dark'
                            )}
                            style={{
                                wordBreak: 'break-all',
                                overflowWrap: 'break-word',
                            }}
                        />

                        {/* Character count indicator */}
                        <div
                            className={cn(
                                'text-caption-1-regular mt-1.5 flex items-center justify-end gap-1 pr-1',
                                isEmpty
                                    ? 'text-gray-400'
                                    : isValidLength
                                      ? 'text-positive'
                                      : charCount > EXPECTED_CHAR_COUNT
                                        ? 'text-negative'
                                        : 'text-gray-500'
                            )}
                        >
                            <span className='font-mono text-xs'>
                                {charCount}/{EXPECTED_CHAR_COUNT}
                            </span>
                            {isValidLength && <IcCheck className='size-3.5' />}
                        </div>
                    </div>

                    {/* Error/Warning messages */}
                    {(hasError || warning) && (
                        <div className='text-caption-1-regular w-full pt-1 pl-1'>
                            {hasError && <span className='text-negative font-bold'>{error[0]}</span>}
                            {warning && (
                                <span className='text-brand-main'>
                                    {hasError && <br />}
                                    {warning}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <StanButton
                name='mfa-recovery-submit'
                type='submit'
                className='mb-2 w-full'
                disabled={!isValidLength || loading}
                loading={loading}
            >
                {submitButtonText}
            </StanButton>

            <div className='pt-5'>
                <p className='text-light text-body-sm text-center'>
                    <button type='button' className='text-brand-main cursor-pointer' onClick={onShowOTP}>
                        Go Back
                    </button>
                </p>
                <p className='text-light text-body-sm mt-2 text-center'>
                    Or{' '}
                    <button type='button' className='text-brand-main cursor-pointer' onClick={openHelpPageInNewTab}>
                        Get Help
                    </button>
                </p>
            </div>
        </form>
    );
}
